/**
 * Parser utility untuk marker BRODI_BUTTON, BRODI_MATERI, BRODI_COMPARE & BRODI_PRICE di response chatbot.
 * BRODI_BUTTON  : (((BRODI_BUTTON={...})))   — delimiter "=" (tombol pilihan)
 * BRODI_MATERI  : (((BRODI_MATERI:{...})))   — delimiter ":" (kartu materi/e-course)
 * BRODI_COMPARE : (((BRODI_COMPARE:{...})))  — delimiter ":" (perbandingan paket program)
 * BRODI_PRICE   : (((BRODI_PRICE:{...})))    — delimiter ":" (satu paket harga/rekomendasi)
 * BRODI_CTA     : (((BRODI_CTA:{...})))      — delimiter ":" (call-to-action daftar program)
 * Kurung tutup 3-4 (defensive, backend kadang kirim 4).
 */

export interface BrodiButtonItem {
  title: string;
}

export interface BrodiButtonData {
  type: "button";
  items: BrodiButtonItem[];
}

export interface ParsedBrodiButton {
  marker: string;
  data: BrodiButtonData;
  startIndex: number;
  endIndex: number;
}

export interface BrodiMateriItem {
  nama_materi?: string;
  nama_membership?: string;
  foto_course?: string;
  url?: string;
}

export interface BrodiMateriData {
  type: "materi";
  membership_id?: number;
  nama_membership?: string;
  items?: BrodiMateriItem[];
}

export interface ParsedBrodiMateri {
  marker: string;
  data: BrodiMateriData;
  startIndex: number;
  endIndex: number;
}

// Point fasilitas: string biasa (struktur lama) atau object {name, status} (struktur baru)
export interface BrodiPointItem {
  name?: string;
  status?: boolean;
}

export interface BrodiCompareItem {
  membership_id?: number;
  title?: string;
  price?: number;
  gimmick_price?: number;
  duration_days?: number;
  is_online?: boolean;
  session_quota?: number;
  is_guaranteed?: boolean;
  points?: (string | BrodiPointItem)[];
  url?: string;
  // Nested program object (new structure)
  program?: {
    id?: number;
    name?: string;
    title?: string;
    image?: string;
  };
}

export interface BrodiCompareData {
  type: "compare";
  // Flat fields (old structure)
  program_id?: number;
  program_title?: string;
  program_image?: string;
  url?: string;
  // Nested program object (new structure)
  program?: {
    id?: number;
    title?: string;
    image?: string;
  };
  items?: BrodiCompareItem[];
}

export interface ParsedBrodiCompare {
  marker: string;
  data: BrodiCompareData;
  startIndex: number;
  endIndex: number;
}

export interface BrodiPriceData {
  type: "price";
  // Flat fields (old structure)
  program_id?: number;
  program_title?: string;
  membership_id?: number;
  title?: string;
  price?: number;
  gimmick_price?: number;
  duration_days?: number;
  is_online?: boolean;
  highlights?: (string | BrodiPointItem)[];
  url?: string;
  // Nested program object (new structure)
  program?: {
    id?: number;
    title?: string;
  };
}

export interface ParsedBrodiPrice {
  marker: string;
  data: BrodiPriceData;
  startIndex: number;
  endIndex: number;
}

export interface BrodiCtaData {
  type: "cta";
  title?: string;
  subtitle?: string;
  url?: string;
  program_id?: number;
  membership_id?: number;
}

export interface ParsedBrodiCta {
  marker: string;
  data: BrodiCtaData;
  startIndex: number;
  endIndex: number;
}

// Regex marker BRODI_BUTTON.
// Terima 3+ kurung tutup + trailing "]"] "}" junk setelah JSON (defensive, backend kadang kirim 4 kurung atau junk).
// Group 1 = payload JSON.
const BRODI_BUTTON_REGEX = /\(\(\(BRODI_BUTTON=(\{[\s\S]*?\})\){3,}[)}]*/g;

// Regex marker BRODI_MATERI (delimiter ":").
// Terima 3+ kurung tutup + trailing "]"] "}" junk setelah JSON (defensive, backend kadang kirim 4 kurung atau junk).
// Group 1 = payload JSON.
const BRODI_MATERI_REGEX = /\(\(\(BRODI_MATERI:(\{[\s\S]*?\})\){3,}[)}]*/g;

// Regex marker BRODI_COMPARE (delimiter ":").
// Terima 3+ kurung tutup + trailing "]"] "}" junk setelah JSON (defensive, backend kadang kirim 4 kurung atau junk).
// Group 1 = payload JSON.
const BRODI_COMPARE_REGEX = /\(\(\(BRODI_COMPARE:(\{[\s\S]*?\})\){3,}[)}]*/g;

// Regex marker BRODI_PRICE (delimiter ":").
// Terima 3+ kurung tutup + trailing "]"] "}" junk setelah JSON (defensive, backend kadang kirim 4 kurung atau junk).
// Group 1 = payload JSON.
const BRODI_PRICE_REGEX = /\(\(\(BRODI_PRICE:(\{[\s\S]*?\})\){3,}[)}]*/g;

// Regex marker BRODI_CTA (delimiter ":").
// Terima 3+ kurung tutup + trailing ")" "}" junk setelah JSON (defensive, backend kadang kirim 4 kurung atau junk).
// Group 1 = payload JSON.
const BRODI_CTA_REGEX = /\(\(\(BRODI_CTA:(\{[\s\S]*?\})\){3,}[)}]*/g;

/**
 * Parse payload JSON marker dengan toleransi terhadap JSON terpotong (unclosed array/object).
 * Backend/LLM kadang memotong JSON di tengah, misal items array tidak ditutup.
 * 1. Coba JSON.parse langsung. Sukses -> return hasil.
 * 2. Gagal -> repair struktural: scan karakter demi karakter (handle string literal & escape),
 *    pakai stack untuk { dan [. Kalau ketemu } atau ] yang TIDAK match isi stack -> return null
 *    (tidak bisa repair). Kalau ada string tidak tertutup -> return null.
 *    Kalau stack masih bersisa, append penutup dengan urutan terbalik (stack berisi {,[ -> suffix ]}).
 * 3. Coba JSON.parse hasil repair. Sukses -> return hasil; gagal -> return null.
 */
export function tryParseMarkerJson(raw: string): any | null {
  if (typeof raw !== "string") return null;

  // 1. Coba parse langsung
  try {
    return JSON.parse(raw);
  } catch {
    // lanjut ke repair struktural
  }

  // 2. Repair struktural: cek struktur yang belum ditutup
  const stack: Array<"{" | "["> = [];
  let inString = false;
  let escaped = false;

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
    } else if (ch === "{") {
      stack.push("{");
    } else if (ch === "[") {
      stack.push("[");
    } else if (ch === "}" || ch === "]") {
      const expected = ch === "}" ? "{" : "[";
      if (stack.length === 0 || stack[stack.length - 1] !== expected) {
        // Penutup tidak match struktur -> payload korup, tidak bisa repair
        return null;
      }
      stack.pop();
    }
  }

  // String literal tidak tertutup -> payload korup, tidak bisa repair
  if (inString) return null;

  // Tidak ada struktur yang kurang -> parse langsung tadi gagal karena hal lain, jangan dipaksakan
  if (stack.length === 0) return null;

  // 3. Tutup struktur yang belum lengkap, urutan terbalik
  const suffix = stack
    .slice()
    .reverse()
    .map((bracket) => (bracket === "{" ? "}" : "]"))
    .join("");

  try {
    return JSON.parse(raw + suffix);
  } catch {
    return null;
  }
}

/**
 * Cek apakah teks mengandung marker BRODI_BUTTON.
 */
export function hasBrodiButtonMarker(text: string): boolean {
  if (!text) return false;

  // Regex global sifatnya stateful (lastIndex) — reset sebelum & sesudah test
  BRODI_BUTTON_REGEX.lastIndex = 0;
  const found = BRODI_BUTTON_REGEX.test(text);
  BRODI_BUTTON_REGEX.lastIndex = 0;

  return found;
}

/**
 * Parse semua marker BRODI_BUTTON dari teks.
 * Return array marker ter-parse beserta posisinya di teks asli.
 */
export function parseBrodiButtons(text: string): ParsedBrodiButton[] {
  if (!text) return [];

  const results: ParsedBrodiButton[] = [];
  let match: RegExpExecArray | null;

  // Reset lastIndex untuk keamanan (regex global stateful)
  BRODI_BUTTON_REGEX.lastIndex = 0;

  while ((match = BRODI_BUTTON_REGEX.exec(text)) !== null) {
    const fullMarker = match[0];
    const jsonPayload = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + fullMarker.length;

    try {
      const parsed = tryParseMarkerJson(jsonPayload) as BrodiButtonData;

      // Validasi struktur: type "button", items array, tiap item punya title string
      if (
        parsed?.type === "button" &&
        Array.isArray(parsed?.items) &&
        parsed.items.every((item) => item && typeof item?.title === "string")
      ) {
        results.push({
          marker: fullMarker,
          data: parsed,
          startIndex,
          endIndex,
        });
      } else {
        console.warn("[BRODI_BUTTON] Struktur tidak valid:", parsed);
      }
    } catch (error) {
      console.warn("[BRODI_BUTTON] Gagal parse JSON payload:", jsonPayload, error);
    }

    // Hindari infinite loop kalau match zero-width
    if (match.index === BRODI_BUTTON_REGEX.lastIndex) {
      BRODI_BUTTON_REGEX.lastIndex++;
    }
  }

  return results;
}

/**
 * Cek apakah teks mengandung marker BRODI_MATERI.
 */
export function hasBrodiMateriMarker(text: string): boolean {
  if (!text) return false;

  // Regex global sifatnya stateful (lastIndex) — reset sebelum & sesudah test
  BRODI_MATERI_REGEX.lastIndex = 0;
  const found = BRODI_MATERI_REGEX.test(text);
  BRODI_MATERI_REGEX.lastIndex = 0;

  return found;
}

/**
 * Parse semua marker BRODI_MATERI dari teks.
 * Return array marker ter-parse beserta posisinya di teks asli.
 */
export function parseBrodiMateris(text: string): ParsedBrodiMateri[] {
  if (!text) return [];

  const results: ParsedBrodiMateri[] = [];
  let match: RegExpExecArray | null;

  // Reset lastIndex untuk keamanan (regex global stateful)
  BRODI_MATERI_REGEX.lastIndex = 0;

  while ((match = BRODI_MATERI_REGEX.exec(text)) !== null) {
    const fullMarker = match[0];
    const jsonPayload = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + fullMarker.length;

    try {
      const parsed = tryParseMarkerJson(jsonPayload) as BrodiMateriData;

      // Validasi struktur: type "materi", items array, tiap item object (bukan array/null)
      if (
        parsed?.type === "materi" &&
        Array.isArray(parsed?.items) &&
        parsed.items.every((item) => item && typeof item === "object" && !Array.isArray(item))
      ) {
        results.push({
          marker: fullMarker,
          data: parsed,
          startIndex,
          endIndex,
        });
      } else {
        console.warn("[BRODI_MATERI] Struktur tidak valid:", parsed);
      }
    } catch (error) {
      console.warn("[BRODI_MATERI] Gagal parse JSON payload:", jsonPayload, error);
    }

    // Hindari infinite loop kalau match zero-width
    if (match.index === BRODI_MATERI_REGEX.lastIndex) {
      BRODI_MATERI_REGEX.lastIndex++;
    }
  }

  return results;
}

/**
 * Cek apakah teks mengandung marker BRODI_COMPARE.
 */
export function hasBrodiCompareMarker(text: string): boolean {
  if (!text) return false;

  // Regex global sifatnya stateful (lastIndex) — reset sebelum & sesudah test
  BRODI_COMPARE_REGEX.lastIndex = 0;
  const found = BRODI_COMPARE_REGEX.test(text);
  BRODI_COMPARE_REGEX.lastIndex = 0;

  return found;
}

/**
 * Parse semua marker BRODI_COMPARE dari teks.
 * Return array marker ter-parse beserta posisinya di teks asli.
 */
export function parseBrodiCompares(text: string): ParsedBrodiCompare[] {
  if (!text) return [];

  const results: ParsedBrodiCompare[] = [];
  let match: RegExpExecArray | null;

  // Reset lastIndex untuk keamanan (regex global stateful)
  BRODI_COMPARE_REGEX.lastIndex = 0;

  while ((match = BRODI_COMPARE_REGEX.exec(text)) !== null) {
    const fullMarker = match[0];
    const jsonPayload = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + fullMarker.length;

    try {
      const parsed = tryParseMarkerJson(jsonPayload) as BrodiCompareData;

      // Validasi struktur: type "compare", items array, tiap item object (bukan array/null)
      if (
        parsed?.type === "compare" &&
        Array.isArray(parsed?.items) &&
        parsed.items.every((item) => item && typeof item === "object" && !Array.isArray(item))
      ) {
        results.push({
          marker: fullMarker,
          data: parsed,
          startIndex,
          endIndex,
        });
      } else {
        console.warn("[BRODI_COMPARE] Struktur tidak valid:", parsed);
      }
    } catch (error) {
      console.warn("[BRODI_COMPARE] Gagal parse JSON payload:", jsonPayload, error);
    }

    // Hindari infinite loop kalau match zero-width
    if (match.index === BRODI_COMPARE_REGEX.lastIndex) {
      BRODI_COMPARE_REGEX.lastIndex++;
    }
  }

  return results;
}

/**
 * Cek apakah teks mengandung marker BRODI_PRICE.
 */
export function hasBrodiPriceMarker(text: string): boolean {
  if (!text) return false;

  // Regex global sifatnya stateful (lastIndex) — reset sebelum & sesudah test
  BRODI_PRICE_REGEX.lastIndex = 0;
  const found = BRODI_PRICE_REGEX.test(text);
  BRODI_PRICE_REGEX.lastIndex = 0;

  return found;
}

/**
 * Parse semua marker BRODI_PRICE dari teks.
 * Return array marker ter-parse beserta posisinya di teks asli.
 */
export function parseBrodiPrices(text: string): ParsedBrodiPrice[] {
  if (!text) return [];

  const results: ParsedBrodiPrice[] = [];
  let match: RegExpExecArray | null;

  // Reset lastIndex untuk keamanan (regex global stateful)
  BRODI_PRICE_REGEX.lastIndex = 0;

  while ((match = BRODI_PRICE_REGEX.exec(text)) !== null) {
    const fullMarker = match[0];
    const jsonPayload = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + fullMarker.length;

    try {
      const parsed = tryParseMarkerJson(jsonPayload) as BrodiPriceData;

      // Validasi struktur: type "price" dan hasil parse berupa object (semua field lain opsional)
      if (parsed?.type === "price" && parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        results.push({
          marker: fullMarker,
          data: parsed,
          startIndex,
          endIndex,
        });
      } else {
        console.warn("[BRODI_PRICE] Struktur tidak valid:", parsed);
      }
    } catch (error) {
      console.warn("[BRODI_PRICE] Gagal parse JSON payload:", jsonPayload, error);
    }

    // Hindari infinite loop kalau match zero-width
    if (match.index === BRODI_PRICE_REGEX.lastIndex) {
      BRODI_PRICE_REGEX.lastIndex++;
    }
  }

  return results;
}

/**
 * Cek apakah teks mengandung marker BRODI_CTA.
 */
export function hasBrodiCtaMarker(text: string): boolean {
  if (!text) return false;

  // Regex global sifatnya stateful (lastIndex) — reset sebelum & sesudah test
  BRODI_CTA_REGEX.lastIndex = 0;
  const found = BRODI_CTA_REGEX.test(text);
  BRODI_CTA_REGEX.lastIndex = 0;

  return found;
}

/**
 * Parse semua marker BRODI_CTA dari teks.
 * Return array marker ter-parse beserta posisinya di teks asli.
 */
export function parseBrodiCtas(text: string): ParsedBrodiCta[] {
  if (!text) return [];

  const results: ParsedBrodiCta[] = [];
  let match: RegExpExecArray | null;

  // Reset lastIndex untuk keamanan (regex global stateful)
  BRODI_CTA_REGEX.lastIndex = 0;

  while ((match = BRODI_CTA_REGEX.exec(text)) !== null) {
    const fullMarker = match[0];
    const jsonPayload = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + fullMarker.length;

    try {
      const parsed = tryParseMarkerJson(jsonPayload) as BrodiCtaData;

      // Validasi struktur: type "cta" dan hasil parse berupa object (semua field lain opsional)
      if (parsed?.type === "cta" && parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        results.push({
          marker: fullMarker,
          data: parsed,
          startIndex,
          endIndex,
        });
      } else {
        console.warn("[BRODI_CTA] Struktur tidak valid:", parsed);
      }
    } catch (error) {
      console.warn("[BRODI_CTA] Gagal parse JSON payload:", jsonPayload, error);
    }

    // Hindari infinite loop kalau match zero-width
    if (match.index === BRODI_CTA_REGEX.lastIndex) {
      BRODI_CTA_REGEX.lastIndex++;
    }
  }

  return results;
}

export interface TextSegment {
  type: "text";
  content: string;
}

export interface ButtonSegment {
  type: "button";
  data: BrodiButtonData;
  marker: string;
}

export interface MateriSegment {
  type: "materi";
  data: BrodiMateriData;
  marker: string;
}

export interface CompareSegment {
  type: "compare";
  data: BrodiCompareData;
  marker: string;
}

export interface PriceSegment {
  type: "price";
  data: BrodiPriceData;
  marker: string;
}

export interface CtaSegment {
  type: "cta";
  data: BrodiCtaData;
  marker: string;
}

/** Segmen placeholder saat SSE: marker (((BRODI_ sudah mulai tapi belum lengkap */
export type BrodiSkeletonKind = "button" | "materi" | "compare" | "price" | "cta" | "unknown";

export interface SkeletonSegment {
  type: "skeleton";
  kind: BrodiSkeletonKind;
}

/** Marker lengkap (regex match) tapi JSON/struktur gagal divalidasi */
export interface FailedSegment {
  type: "failed";
  kind: BrodiSkeletonKind;
  marker: string;
}

export type ParsedSegment =
  | TextSegment
  | ButtonSegment
  | MateriSegment
  | CompareSegment
  | PriceSegment
  | CtaSegment
  | SkeletonSegment
  | FailedSegment;

// Marker hasil parse (button/materi/compare/price) yang digabung & diurutkan berdasarkan posisi di teks asli
// Gunakan explicit union (bukan intersection) agar discriminant `kind` bisa di-narrow TS di semua versi
type ParsedMarker =
  | { kind: "button"; marker: string; data: BrodiButtonData; startIndex: number; endIndex: number }
  | { kind: "materi"; marker: string; data: BrodiMateriData; startIndex: number; endIndex: number }
  | { kind: "compare"; marker: string; data: BrodiCompareData; startIndex: number; endIndex: number }
  | { kind: "price"; marker: string; data: BrodiPriceData; startIndex: number; endIndex: number }
  | { kind: "cta"; marker: string; data: BrodiCtaData; startIndex: number; endIndex: number }
  | { kind: "failed"; marker: string; data?: undefined; startIndex: number; endIndex: number; failKind: BrodiSkeletonKind };

type BrodiHelperKind = "button" | "materi" | "compare" | "price" | "cta";

type MarkerHit = {
  kind: BrodiHelperKind;
  startIndex: number;
  endIndex: number;
  fullMarker: string;
  jsonPayload: string;
};

/** Deteksi jenis skeleton dari fragmen marker yang belum lengkap */
export function detectBrodiSkeletonKind(fragment: string): BrodiSkeletonKind {
  const upper = (fragment || "").toUpperCase();
  if (upper.includes("BRODI_BUTTON")) return "button";
  if (upper.includes("BRODI_MATERI")) return "materi";
  if (upper.includes("BRODI_COMPARE")) return "compare";
  if (upper.includes("BRODI_PRICE")) return "price";
  if (upper.includes("BRODI_CTA")) return "cta";
  return "unknown";
}

function isValidButtonData(parsed: any): parsed is BrodiButtonData {
  return (
    parsed?.type === "button" &&
    Array.isArray(parsed?.items) &&
    parsed.items.every((item: any) => item && typeof item?.title === "string")
  );
}

function isValidMateriData(parsed: any): parsed is BrodiMateriData {
  return parsed?.type === "materi" && parsed && typeof parsed === "object" && !Array.isArray(parsed);
}

function isValidCompareData(parsed: any): parsed is BrodiCompareData {
  return parsed?.type === "compare" && parsed && typeof parsed === "object" && !Array.isArray(parsed);
}

function isValidPriceData(parsed: any): parsed is BrodiPriceData {
  return parsed?.type === "price" && parsed && typeof parsed === "object" && !Array.isArray(parsed);
}

function isValidCtaData(parsed: any): parsed is BrodiCtaData {
  return parsed?.type === "cta" && parsed && typeof parsed === "object" && !Array.isArray(parsed);
}

/**
 * Ubah hit marker lengkap → segment valid, atau `failed` jika JSON/struktur invalid.
 */
export function completeMarkerHitToSegment(hit: MarkerHit): ParsedSegment {
  const parsed = tryParseMarkerJson(hit.jsonPayload);

  if (hit.kind === "button") {
    if (isValidButtonData(parsed)) {
      return { type: "button", data: parsed, marker: hit.fullMarker };
    }
  } else if (hit.kind === "materi") {
    if (isValidMateriData(parsed)) {
      return { type: "materi", data: parsed, marker: hit.fullMarker };
    }
  } else if (hit.kind === "compare") {
    if (isValidCompareData(parsed)) {
      return { type: "compare", data: parsed, marker: hit.fullMarker };
    }
  } else if (hit.kind === "price") {
    if (isValidPriceData(parsed)) {
      return { type: "price", data: parsed, marker: hit.fullMarker };
    }
  } else if (hit.kind === "cta") {
    if (isValidCtaData(parsed)) {
      return { type: "cta", data: parsed, marker: hit.fullMarker };
    }
  }

  return {
    type: "failed",
    kind: hit.kind,
    marker: hit.fullMarker,
  };
}

/**
 * Kumpulkan semua hit marker BRODI lengkap (regex match) di teks, tanpa tumpang-tindih.
 */
function collectCompleteMarkerHits(text: string): MarkerHit[] {
  if (!text) return [];

  const configs: { kind: BrodiHelperKind; regex: RegExp }[] = [
    { kind: "button", regex: BRODI_BUTTON_REGEX },
    { kind: "materi", regex: BRODI_MATERI_REGEX },
    { kind: "compare", regex: BRODI_COMPARE_REGEX },
    { kind: "price", regex: BRODI_PRICE_REGEX },
    { kind: "cta", regex: BRODI_CTA_REGEX },
  ];

  const hits: MarkerHit[] = [];

  for (const { kind, regex } of configs) {
    regex.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      hits.push({
        kind,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
        fullMarker: match[0],
        jsonPayload: match[1],
      });
      if (match.index === regex.lastIndex) {
        regex.lastIndex++;
      }
    }
    regex.lastIndex = 0;
  }

  hits.sort((a, b) => a.startIndex - b.startIndex || b.endIndex - a.endIndex);

  // Buang overlap: hit yang mulai sebelum end hit sebelumnya
  const nonOverlapping: MarkerHit[] = [];
  let cursor = 0;
  for (const hit of hits) {
    if (hit.startIndex < cursor) continue;
    nonOverlapping.push(hit);
    cursor = hit.endIndex;
  }

  return nonOverlapping;
}

/**
 * Cek apakah string (dari index 0) diawali marker BRODI lengkap (regex tutup sudah cocok).
 * Dipakai membedakan trailing incomplete vs marker lengkap yang gagal validasi struktur.
 */
function startsWithCompleteBrodiMarker(fragment: string): boolean {
  if (!fragment) return false;
  const regexes = [
    BRODI_BUTTON_REGEX,
    BRODI_MATERI_REGEX,
    BRODI_COMPARE_REGEX,
    BRODI_PRICE_REGEX,
    BRODI_CTA_REGEX,
  ];
  for (const regex of regexes) {
    regex.lastIndex = 0;
    const match = regex.exec(fragment);
    regex.lastIndex = 0;
    if (match && match.index === 0) return true;
  }
  return false;
}

function matchCompleteBrodiAtStart(fragment: string): MarkerHit | null {
  const configs: { kind: BrodiHelperKind; regex: RegExp }[] = [
    { kind: "button", regex: BRODI_BUTTON_REGEX },
    { kind: "materi", regex: BRODI_MATERI_REGEX },
    { kind: "compare", regex: BRODI_COMPARE_REGEX },
    { kind: "price", regex: BRODI_PRICE_REGEX },
    { kind: "cta", regex: BRODI_CTA_REGEX },
  ];
  for (const { kind, regex } of configs) {
    regex.lastIndex = 0;
    const match = regex.exec(fragment);
    regex.lastIndex = 0;
    if (match && match.index === 0) {
      return {
        kind,
        startIndex: 0,
        endIndex: match[0].length,
        fullMarker: match[0],
        jsonPayload: match[1],
      };
    }
  }
  return null;
}

/**
 * Split teks menjadi segmen teks biasa, data BRODI_*, failed (gagal parse), dan (opsional) skeleton saat streaming.
 * streaming=true: fragment `(((BRODI_...` yang belum tertutup diganti skeleton, tidak ditampilkan sebagai raw text.
 */
export function parseTextWithButtons(
  text: string,
  options?: { streaming?: boolean }
): ParsedSegment[] {
  if (!text) return [{ type: "text", content: "" }];

  const streaming = options?.streaming === true;
  const hits = collectCompleteMarkerHits(text);

  if (hits.length === 0) {
    // Tidak ada marker lengkap — cek fragment streaming di manapun di text
    if (streaming) {
      return buildStreamingSegmentsWithoutCompleteMarkers(text);
    }
    return [{ type: "text", content: text }];
  }

  const segments: ParsedSegment[] = [];
  let lastEnd = 0;

  for (const hit of hits) {
    if (hit.startIndex > lastEnd) {
      const between = text.slice(lastEnd, hit.startIndex);
      if (streaming) {
        segments.push(...splitTextWithIncompleteBrodi(between));
      } else if (between.length > 0) {
        segments.push({ type: "text", content: between });
      }
    }

    segments.push(completeMarkerHitToSegment(hit));
    lastEnd = hit.endIndex;
  }

  if (lastEnd < text.length) {
    const tail = text.slice(lastEnd);
    if (streaming) {
      segments.push(...splitTextWithIncompleteBrodi(tail));
    } else if (tail.length > 0) {
      segments.push({ type: "text", content: tail });
    }
  }

  return segments.filter((seg) => {
    if (seg.type === "text") {
      return seg.content.length > 0 || segments.length === 1;
    }
    return true;
  });
}

/**
 * Teks tanpa marker lengkap: potong di (((BRODI_ lalu skeleton + hilangkan raw marker.
 */
function buildStreamingSegmentsWithoutCompleteMarkers(text: string): ParsedSegment[] {
  return splitTextWithIncompleteBrodi(text).filter((seg) => {
    if (seg.type === "text") return seg.content.length > 0;
    return true;
  });
}

/**
 * Split string: teks biasa + skeleton untuk tiap fragment (((BRODI_ yang belum lengkap.
 * Marker yang regex-nya sudah “lengkap” (tapi gagal validasi) dibiarkan sebagai text (fallback).
 */
function splitTextWithIncompleteBrodi(text: string): ParsedSegment[] {
  if (!text) return [];

  const segments: ParsedSegment[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    const startRel = remaining.search(/\(\(\(BRODI_/i);
    if (startRel === -1) {
      segments.push({ type: "text", content: remaining });
      break;
    }

    if (startRel > 0) {
      segments.push({ type: "text", content: remaining.slice(0, startRel) });
    }

    const fromMarker = remaining.slice(startRel);

    // Marker regEx-lengkap di posisi 0: coba map ke helper valid, atau failed (bukan raw text)
    if (startsWithCompleteBrodiMarker(fromMarker)) {
      const hit = matchCompleteBrodiAtStart(fromMarker);
      if (hit) {
        segments.push(completeMarkerHitToSegment(hit));
        remaining = fromMarker.slice(hit.endIndex);
        continue;
      }
      // fallback defensive
      remaining = fromMarker.slice(1);
      continue;
    }

    // Incomplete marker: skeleton, sisa setelah marker lain (kalau ada start berikutnya) diproses loop
    // Biasanya stream hanya punya satu incomplete di ujung — consume sampai keesokan (((BRODI_ atau selesai
    const nextStart = fromMarker.slice(3).search(/\(\(\(BRODI_/i); // skip current (((
    if (nextStart === -1) {
      segments.push({
        type: "skeleton",
        kind: detectBrodiSkeletonKind(fromMarker),
      });
      break;
    }

    // Ada marker ke-2 tanpa penutup yang pertama (jarang) — skeleton untuk yang pertama, lanjut sisa
    const firstChunkLen = 3 + nextStart;
    segments.push({
      type: "skeleton",
      kind: detectBrodiSkeletonKind(fromMarker.slice(0, firstChunkLen)),
    });
    remaining = fromMarker.slice(firstChunkLen);
  }

  return segments;
}

/**
 * Konversi URL payload materi menjadi path internal App.
 * Contoh: "https://brofesional.id/classes/e-course/4/9" → "/classes/e-course/4/9"
 * Hanya memakai pathname (bukan hostname), jadi aman dipakai di environment development.
 * Return null kalau URL tidak dikenal / bukan route e-course.
 */
export function toInternalEcoursePath(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;

  let path = url;
  try {
    if (/^https?:\/\//i.test(url)) {
      path = new URL(url).pathname;
    }
  } catch {
    return null;
  }

  if (!path.startsWith("/")) return null;
  // Buang trailing slash agar cocok dengan pattern route
  path = path.replace(/\/+$/, "");

  // Route e-course internal: /classes/e-course/{membershipId}/{courseId}[/{materiId}[/{id}]]
  if (!/^\/classes\/e-course\/\d+\/\d+(\/\d+(\/\d+)?)?$/.test(path)) return null;

  return path;
}

/**
 * Konversi URL payload program menjadi path internal App.
 * Contoh: "https://brofesional.id/program/6" → "/program/6"
 * Terima juga path relatif seperti "/program/6" (dipakai fallback dari program_id).
 * Hanya memakai pathname (bukan hostname), jadi aman dipakai di environment development.
 * Return null kalau URL tidak dikenal / bukan route program.
 */
export function toInternalProgramPath(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;

  let path = url;
  try {
    if (/^https?:\/\//i.test(url)) {
      path = new URL(url).pathname;
    }
  } catch {
    return null;
  }

  if (!path.startsWith("/")) return null;
  // Buang trailing slash agar cocok dengan pattern route
  path = path.replace(/\/+$/, "");

  // Route program internal: /program/{id}
  if (!/^\/program\/\d+$/.test(path)) return null;

  return path;
}
